import { useState } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { useOrder, useQuickEditIdentity } from '../features/orders.js';
import { storeOrderFiles } from '../features/orderFiling.js';
import { useDateFormatter } from '../lib/format.js';
import { ErrorState, InlineError, LoadingState, Modal } from './ui.js';

/**
 * edit only the order's identity fields — customer, vessel name, SAP — which every department may
 * change even while the order is locked. saving notifies Assembly (in-app + email); a confirmed order is
 * filed again under its new SAP / name (the worker moves its Order Sheet row and order file), and its
 * workbooks are made again here, best-effort (the order's page can file it again).
 */
export function IdentityEditModal({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, error } = useOrder(id);
  const edit = useQuickEditIdentity();
  const { me } = useAuth();
  const df = useDateFormatter();
  const [customer, setCustomer] = useState<string | null>(null);
  const [vesselName, setVesselName] = useState<string | null>(null);
  const [sap, setSap] = useState<string | null>(null);

  const s = data?.snapshot;
  const cust = customer ?? s?.buyer?.name ?? '';
  const vess = vesselName ?? s?.vessel?.vesselName ?? '';
  const sapV = sap ?? (s?.vessel?.sapNumber && s.vessel.sapNumber !== '?' ? s.vessel.sapNumber : '');

  const save = () => {
    edit.mutate({ id, customer: cust, vesselName: vess, sapNumber: sapV }, {
      onSuccess: (res) => {
        const f = res.orderFile;
        if (f) void storeOrderFiles(f, `Revision ${f.revision} · ${df.dateTime(new Date().toISOString())} · ${me?.user.displayName || me?.user.email || ''}`).catch(() => undefined);
        onClose();
      },
    });
  };

  return (
    <Modal
      title="Edit order details"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={edit.isPending || !data} onClick={save}>
            {edit.isPending ? 'Saving…' : 'Save details'}
          </button>
        </>
      }
    >
      {!data && !error && <LoadingState />}
      {error && <ErrorState error={error} />}
      {data && (
        <>
          <label style={{ display: 'block' }}>
            <span className="field-label">Customer</span>
            <input className="field" style={{ width: '100%' }} autoComplete="off" value={cust} onChange={(e) => setCustomer(e.target.value)} />
          </label>
          <label style={{ display: 'block', marginTop: 8 }}>
            <span className="field-label">Vessel name</span>
            <input className="field" style={{ width: '100%' }} value={vess} onChange={(e) => setVesselName(e.target.value)} />
          </label>
          <label style={{ display: 'block', marginTop: 8 }}>
            <span className="field-label">SAP</span>
            <input
              className="field"
              style={{ width: '100%' }}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="done"
              value={sapV}
              onChange={(e) => setSap(e.target.value)}
            />
          </label>
          <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
            Allowed even when the order is locked. Assembly is told, and a confirmed order’s Order Sheet row and order file follow.
          </p>
          {edit.isError && <InlineError error={edit.error} onDismiss={() => edit.reset()} />}
        </>
      )}
    </Modal>
  );
}
